import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, enableProdMode, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter } from '@angular/router';
import { NgbDateParserFormatter, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgIdleModule } from '@ng-idle/core';
import { definePreset } from '@primeuix/themes';
import Nora from '@primeuix/themes/nora'
import { AutocompleteLibModule } from 'angular-ng-autocomplete';
import { LuxonModule } from 'luxon-angular';
import { provideScrollbarOptions } from 'ngx-scrollbar';
import { providePrimeNG } from 'primeng/config';
import { AppComponent } from './app/app.component';
import { BasicAuthInterceptorService } from './app/helper/basic-auth-interceptor.service';
import { ErrorInterceptorService } from './app/helper/error-interceptor.service';
import { NgbDateCustomParserFormatter } from './app/helper/ngbdate-custom-parser-formatter';
import { DateFormatService } from './app/service/date-format.service';
import { environment } from './environments/environment';

export function initDateFormat(service: DateFormatService) {
  return () => service.loadFormat();
}

if (environment.production) {
  enableProdMode();
}

let routesPromise;
if (environment.maintenance) {
  routesPromise = import('./app/routes.maintenance').then(m => m.routes);
}
else {
  routesPromise = import('./app/routes').then(m => m.routes);
}

const borderRadius = '0.375rem';
const borderColor = '#dee2e6';
const textColor = '#676a6c';
const linkColor = '#0088ca';

const darkBluePreset = definePreset(Nora, {
  semantic: {
    primary: {
      50: '{sky.50}',
      100: '{sky.100}',
      200: '{sky.200}',
      300: '{sky.300}',
      400: '{sky.400}',
      500: '{sky.500}',
      600: '{sky.600}',
      700: '{sky.700}',
      800: '{sky.800}',
      900: '{sky.900}',
      950: '{sky.950}'
    }
  },
  components: {
    datatable: {
      bodyCell: {
        borderColor: borderColor
      },
      headerCell: {
        color: textColor,
        selectedBackground: 'unset',
        selectedColor: linkColor,
        borderColor: borderColor,
      },
      sortIcon: {
        color: '#b3b4b5',
      },
      row: {
        color: textColor,
      }
    },
    inputtext: {
      root: {
        borderColor: borderColor,
        borderRadius: borderRadius,
        color: textColor,
      }
    },
    select: {
      root: {
        borderColor: borderColor,
        borderRadius: borderRadius,
        color: textColor,
      },
      option: {
        color: textColor,
      }
    }
  }
});

routesPromise.then(routes => {
  bootstrapApplication(
    AppComponent,
    {
      providers: [
        provideZoneChangeDetection(),
        provideAnimationsAsync(),
        providePrimeNG({
          theme: {preset: darkBluePreset}
        }),
        provideRouter(routes),
        provideScrollbarOptions({
          visibility: 'hover',
          appearance: 'compact'
        }),
        importProvidersFrom(
          NgbModule,
          AutocompleteLibModule,
          LuxonModule,
          NgIdleModule.forRoot()
        ),
        {
          provide: HTTP_INTERCEPTORS,
          useClass: ErrorInterceptorService,
          multi: true,
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: BasicAuthInterceptorService,
          multi: true,
        },
        {
          provide: APP_INITIALIZER,
          useFactory: initDateFormat,
          deps: [DateFormatService],
          multi: true
        },
        {
          provide: NgbDateParserFormatter,
          useClass: NgbDateCustomParserFormatter
        },
        provideHttpClient(withInterceptorsFromDi()),
      ]
    }
  ).catch(err => console.error(err));
});
